import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { inventoryApi, medallionColorApi } from '../../services/api'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import ImageUploader from '../../components/common/ImageUploader'

/**
 * ItemEditModal — used both for "+ New Item" (item is `{}`) and "Edit"
 * (item is a full row).
 *
 * Medallion-specific fields:
 *   - When category = 'Medallions', a "Medallion Type" dropdown appears
 *     (Bronze / Triplate / Laser-Etched / Eternity / Accessory)
 *   - When medallion type = 'Triplate', a "Color" dropdown appears
 *     sourced from the global medallion_colors list
 *   - For non-medallion items, type and color stay hidden (color is just
 *     the free-text variant field as before)
 *
 * The image uploader only shows in edit mode — to upload an image, the
 * item must already have an id. For new items we save first, then
 * the user clicks Edit to add the image.
 */

const MEDALLION_CATEGORY = 'Medallions'
const MEDALLION_TYPES = ['Bronze', 'Triplate', 'Laser-Etched', 'Eternity', 'Accessory']

export default function ItemEditModal({ item, categories, onClose, onSaved }) {
    const isNew = !item.id

    const [form, setForm] = useState({
        itemNumber: item.itemNumber || '',
        category: item.category || '',
        medallionType: item.medallionType || '',
        description: item.description || '',
        cost: item.cost?.toString() ?? '',
        quantity: item.quantity?.toString() ?? '0',
        stockLimit: item.stockLimit?.toString() ?? '0',
        year: item.year || '',
        color: item.color || '',
        isNew: item.isNew ?? false,
        isFeatured: item.isFeatured ?? false,
    })

    // Local mirror of the item's image URL — updated optimistically when the
    // uploader fires onChange so the modal reflects the new state without
    // forcing a full reload.
    const [imageUrl, setImageUrl] = useState(item.imageUrl || null)

    const [usingNewCategory, setUsingNewCategory] = useState(
        !!item.category && categories.length > 0 && !categories.includes(item.category)
    )

    const [medallionColors, setMedallionColors] = useState([])
    const [saving, setSaving] = useState(false)

    // Load medallion colors so the Triplate color dropdown can populate.
    useEffect(() => { loadColors() }, [])

    async function loadColors() {
        try {
            const response = await medallionColorApi.list()
            if (response.success) setMedallionColors(response.colors)
        } catch { /* non-fatal */ }
    }

    function update(field, value) {
        setForm(f => ({ ...f, [field]: value }))
    }

    // Derived flags — control which medallion-specific fields are visible.
    const isMedallion = form.category.trim() === MEDALLION_CATEGORY
    const isTriplate = isMedallion && form.medallionType === 'Triplate'

    function handleCategoryChange(newCategory) {
        if (newCategory === '__new__') {
            setUsingNewCategory(true)
            update('category', '')
        } else {
            update('category', newCategory)
            if (newCategory !== MEDALLION_CATEGORY) {
                setForm(f => ({ ...f, category: newCategory, medallionType: '' }))
            }
        }
    }

    function handleMedallionTypeChange(newType) {
        if (newType !== 'Triplate') {
            setForm(f => ({ ...f, medallionType: newType, color: '' }))
        } else {
            update('medallionType', newType)
        }
    }

    async function handleSubmit(e) {
        e.preventDefault()

        if (!form.itemNumber.trim() || !form.category.trim() || !form.description.trim()) {
            toast.error('Item number, category, and description are required')
            return
        }
        if (isMedallion && !form.medallionType) {
            toast.error('Please pick the medallion type')
            return
        }
        if (isTriplate && !form.color.trim()) {
            toast.error('Please pick a color for the Triplate medallion')
            return
        }

        const cost = parseFloat(form.cost)
        if (isNaN(cost) || cost < 0) {
            toast.error('Cost must be a non-negative number')
            return
        }
        const quantity = parseInt(form.quantity, 10)
        if (isNaN(quantity) || quantity < 0) {
            toast.error('Quantity must be a non-negative integer')
            return
        }
        const stockLimit = parseInt(form.stockLimit, 10)
        if (isNaN(stockLimit) || stockLimit < 0) {
            toast.error('Stock limit must be a non-negative integer')
            return
        }

        const payload = {
            itemNumber: form.itemNumber.trim(),
            category: form.category.trim(),
            medallionType: isMedallion ? form.medallionType : null,
            description: form.description.trim(),
            cost,
            quantity,
            stockLimit,
            year: form.year.trim() || null,
            color: form.color.trim() || null,
            isNew: form.isNew,
            isFeatured: form.isFeatured,
        }

        setSaving(true)
        try {
            const response = isNew
                ? await inventoryApi.create(payload)
                : await inventoryApi.update(item.id, payload)

            if (response.success) {
                toast.success(isNew ? 'Item created' : 'Item updated')
                onSaved()
            } else {
                toast.error(response.message || 'Save failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-ghasc-navy">
                        {isNew ? 'New Item' : `Edit: ${item.description}`}
                    </h3>

                    {/* Identity */}
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input id="item-number" label="Item number" value={form.itemNumber}
                            onChange={(e) => update('itemNumber', e.target.value)} required autoFocus />

                        {usingNewCategory ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <Input id="item-category" value={form.category}
                                    onChange={(e) => update('category', e.target.value)}
                                    placeholder="New category name"
                                    required />
                                <button type="button"
                                    onClick={() => setUsingNewCategory(false)}
                                    className="text-xs text-ghasc-navy hover:underline mt-1">
                                    Choose existing category instead
                                </button>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <select
                                    value={form.category}
                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                    required
                                >
                                    <option value="">— Choose category —</option>
                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                    <option value="__new__">+ New category…</option>
                                </select>
                            </div>
                        )}
                    </div>

                    <Input id="item-description" label="Description" value={form.description}
                        onChange={(e) => update('description', e.target.value)} required />

                    {/* Medallion-specific fields */}
                    {isMedallion && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                            <p className="text-sm font-medium text-blue-900">Medallion details</p>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Medallion Type <span className="text-ghasc-danger">*</span>
                                </label>
                                <select
                                    value={form.medallionType}
                                    onChange={(e) => handleMedallionTypeChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                    required
                                >
                                    <option value="">— Select type —</option>
                                    {MEDALLION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>

                            {isTriplate && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Color <span className="text-ghasc-danger">*</span>
                                    </label>
                                    <select
                                        value={form.color}
                                        onChange={(e) => update('color', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                        required
                                    >
                                        <option value="">— Select color —</option>
                                        {medallionColors.map(c => (
                                            <option key={c.id} value={c.name}>{c.name}</option>
                                        ))}
                                        {form.color && !medallionColors.find(c => c.name === form.color) && (
                                            <option value={form.color}>{form.color} (inactive)</option>
                                        )}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Manage the full list in Admin Config → Medallion Colors.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Numbers */}
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Input id="item-cost" label="Cost ($)" type="number" step="0.01" min="0"
                            value={form.cost} onChange={(e) => update('cost', e.target.value)} required />
                        <Input id="item-qty" label="Quantity in stock" type="number" min="0"
                            value={form.quantity} onChange={(e) => update('quantity', e.target.value)} required />
                        <Input id="item-stocklimit" label="Stock limit (par)" type="number" min="0"
                            value={form.stockLimit} onChange={(e) => update('stockLimit', e.target.value)} />
                    </div>
                    <p className="text-xs text-gray-500 -mt-2">
                        <strong>Stock limit</strong>: when quantity drops below this, the item appears on the
                        Reorder page. Set to 0 to exclude from auto-reorder suggestions.
                    </p>

                    {/* Variants — only relevant for non-medallion items */}
                    {!isMedallion && (
                        <fieldset className="border border-gray-200 rounded-lg p-3">
                            <legend className="text-sm font-medium text-gray-700 px-1">Variant (optional)</legend>
                            <p className="text-xs text-gray-500 mb-2">
                                Use these for items where the same item number repeats with different
                                years or colors (e.g. key tags). Leave blank for ordinary items.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <Input id="item-year" label="Year" value={form.year}
                                    onChange={(e) => update('year', e.target.value)}
                                    placeholder="e.g. 1 Year" />
                                <Input id="item-color" label="Color" value={form.color}
                                    onChange={(e) => update('color', e.target.value)}
                                    placeholder="e.g. Bronze" />
                            </div>
                        </fieldset>
                    )}

                    {isMedallion && (
                        <Input id="item-year" label="Year (e.g. '1 Year', '5 Year')" value={form.year}
                            onChange={(e) => update('year', e.target.value)}
                            placeholder="Optional — leave blank for accessories without a year" />
                    )}

                    {/* Flags */}
                    <div className="space-y-2 pt-1">
                        <label className="flex items-center text-sm">
                            <input
                                type="checkbox"
                                checked={form.isNew}
                                onChange={(e) => update('isNew', e.target.checked)}
                                className="mr-2"
                            />
                            Mark as <strong className="mx-1">new</strong> (shows a "new" badge in the catalog)
                        </label>
                        <label className="flex items-center text-sm">
                            <input
                                type="checkbox"
                                checked={form.isFeatured}
                                onChange={(e) => update('isFeatured', e.target.checked)}
                                className="mr-2"
                            />
                            Mark as <strong className="mx-1">featured</strong> (appears in the Featured Items section on the Shop and Dashboard)
                        </label>
                    </div>

                    {!isNew && (
                        <fieldset className="border border-gray-200 rounded-lg p-3">
                            <legend className="text-sm font-medium text-gray-700 px-1">Image</legend>
                            <ImageUploader
                                currentUrl={imageUrl}
                                onUpload={(file) => inventoryApi.uploadImage(item.id, file)}
                                onDelete={() => inventoryApi.deleteImage(item.id)}
                                onChange={setImageUrl}
                                imageClassName="max-h-32 max-w-full"
                                layout="compact"
                            />
                        </fieldset>
                    )}

                    {isNew && (
                        <p className="text-xs text-gray-500">
                            Save the item first, then click Edit to attach an image.
                        </p>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                        <Button type="submit" loading={saving}>
                            {isNew ? 'Create Item' : 'Save'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}